import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Avatar, Box, IconButton, Paper, Skeleton, Stack, Tab, Tabs, Tooltip, Typography, useMediaQuery } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MetricValue from './Dashboard/MetricValue';
import { useAuth } from '../auth/AuthProvider';
import useOrganizationOverview from '../hooks/useOrganizationOverview';
import { overviewErrorMessage } from '../services/overview';
import OverviewHistory from './Dashboard/OverviewHistory';
import MonthlyActivity from './Dashboard/MonthlyActivity';
import TodayAgenda from './Dashboard/TodayAgenda';
import AdministrativeTeam from './Dashboard/AdministrativeTeam';
import i18n, { t } from '../i18n';

const metrics = [
    ['doctors', 'Médicos', MedicalServicesOutlinedIcon],
    ['healthcare_staff', 'Técnicos de saúde', HealthAndSafetyOutlinedIcon],
    ['patients', 'Pacientes registados', PeopleAltOutlinedIcon],
    ['ongoing_patient_plans', 'Planos em curso', AssignmentOutlinedIcon]
];
const panelStyle = {
    p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, borderColor: '#E0E5E9',
    minHeight: 0, minWidth: 0, height: '100%'
};

export default function Home() {
    const { user, role } = useAuth();
    return <OrganizationHome key={[user.uuid, role.uuid, role.account.uuid].join(':')} actor={{ user, role }} />;
}
function OrganizationHome({ actor }) {
    const { role } = actor;
    const account = role.account;
    const client = useQueryClient();
    const overview = useOrganizationOverview(actor);
    const statistics = overview.isError ? null : overview.data;
    const refresh = () => {
        void client.invalidateQueries({ queryKey: ['clinic-overview', actor.user.uuid, role.uuid, account.uuid] });
    };
    const compact = useMediaQuery('(max-width:1199.95px), (max-height:699.95px)');
    const short = useMediaQuery('(max-height:499.95px)');
    const [tab, setTab] = useState('patients');
    const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'pt-PT';
    const format = (value) => new Intl.NumberFormat(locale).format(value);
    const sections = [
        ['patients', 'Pacientes'], ['plans', 'Planos'], ['activity', 'Atividade'],
        ['daily', role.name === 'CLINIC_ADMIN' ? 'Agenda' : 'Equipa']
    ];
    const panel = (name) => {
        if (name === 'patients' || name === 'plans') return <OverviewHistory actor={actor} kind={name} overview={overview} />;
        if (name === 'activity') return <MonthlyActivity />;
        if (role.name === 'CLINIC_ADMIN') return <TodayAgenda />;
        return <AdministrativeTeam overview={overview} />;
    };
    return <Box sx={{
        width: '100%', height: { xs: 'calc(100dvh - 112px)', sm: 'calc(100dvh - 128px)' },
        display: 'grid', minHeight: 0, gap: short ? 1 : { xs: 1.5, sm: 2 },
        gridTemplateRows: compact ? 'auto auto minmax(0, 1fr)' : '40px 82px minmax(0, 1.35fr) minmax(0, 1fr)'
    }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                <Avatar src={account.picture || undefined} alt="" sx={{
                    width: short ? 28 : 36, height: short ? 28 : 36, bgcolor: '#EAF3FC', color: 'accent.main',
                    '& img': { objectFit: 'contain' }
                }}>{account.name?.[0]}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h2" variant="h3" color="primary.main" noWrap title={account.name}
                        sx={{ fontSize: { xs: 16, sm: 20 } }}>{account.name}</Typography>
                    {overview.isError && <Typography role="alert" variant="caption" color="error.main" noWrap component="p"
                        title={t(overviewErrorMessage(overview.error))} sx={{ fontSize: 11 }}>
                        {t(overviewErrorMessage(overview.error))}
                    </Typography>}
                </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
                <Tooltip title={t(overview.isError ? 'Tentar novamente' : 'Atualizar estatísticas')}>
                    <span><IconButton color={overview.isError ? 'warning' : 'default'} disabled={overview.isFetching} onClick={refresh}
                        aria-label={t(overview.isError ? 'Tentar novamente' : 'Atualizar estatísticas')} size="small"><RefreshIcon /></IconButton></span>
                </Tooltip>
                <IconButton component={Link} to="/organization" aria-label={t('Ver organização')} size="small"
                    sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
                    <ChevronRightIcon />
                </IconButton>
            </Stack>
        </Stack>
        <Box sx={{
            display: 'grid', gridTemplateColumns: short ? 'repeat(4, minmax(0, 1fr))'
                : { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: short ? 1 : 1.5
        }}>
            {metrics.map(([key, label, Icon]) => {
                const value = statistics?.[key];
                return <Paper key={key} variant="outlined" title={t('Totais de perfis não desativados.')} sx={{
                    ...panelStyle, p: short ? 1 : { xs: 1.25, sm: 1.75 },
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(7, 26, 46, 0.04)'
                }}>
                    <MetricValue icon={Icon} label={label} short={short}
                        value={overview.isPending ? <Skeleton width={48} sx={{ animationDuration: '2.2s', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} /> : value == null ? '—' : format(value)} />
                </Paper>;
            })}
        </Box>
        {compact ? <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" aria-label={t('Visão geral da organização')}
                sx={{ minHeight: short ? 30 : 38, flexShrink: 0, '& .MuiTab-root': {
                    minWidth: 0, minHeight: short ? 30 : 38, px: 0.5, py: 0.5, fontSize: 12, textTransform: 'none'
                } }}>
                {sections.map(([value, label]) => <Tab key={value} id={'home-tab-' + value}
                    aria-controls={'home-panel-' + value} value={value} label={t(label)} />)}
            </Tabs>
            <Paper component="section" role="tabpanel" id={'home-panel-' + tab} aria-labelledby={'home-tab-' + tab}
                variant="outlined" sx={{ ...panelStyle, flex: 1, p: short ? 1 : panelStyle.p }}>
                {panel(tab)}
            </Paper>
        </Box> : <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, minHeight: 0 }}>
                <Paper component="section" variant="outlined" sx={panelStyle}>{panel('patients')}</Paper>
                <Paper component="section" variant="outlined" sx={panelStyle}>{panel('plans')}</Paper>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: 2, minHeight: 0 }}>
                <Paper component="section" variant="outlined" sx={panelStyle}>{panel('activity')}</Paper>
                <Paper component="section" variant="outlined" sx={panelStyle}>{panel('daily')}</Paper>
            </Box>
        </>}
    </Box>;
}

OrganizationHome.propTypes = { actor: PropTypes.object.isRequired };
